import { inject, Injectable } from '@angular/core';

import { WINDOW } from '@hglobal/fin-ui';

/**
 * Types of card actions that can be synchronized across browser tabs.
 */
export type CardActionSyncAction = 'blockCard' | 'unblockCard' | 'closeCard' | 'reissueCard';

/**
 * State of a card action synchronization event.
 * - `started`: Action has begun execution
 * - `finished`: Action has completed execution
 */
export type CardActionSyncState = 'started' | 'finished';

/**
 * Event payload for card action synchronization.
 */
export interface CardActionSyncEvent {
  /** The unique identifier of the card being acted upon */
  cardId: string;
  /** The type of action being performed */
  action: CardActionSyncAction;
  /** The current state of the action */
  state: CardActionSyncState;
}

/**
 * Service for synchronizing card actions across multiple browser tabs/windows.
 *
 * Uses the BroadcastChannel API to communicate card action state changes between
 * different instances of the application running in separate tabs or windows.
 *
 */
@Injectable({
  providedIn: 'root',
})
export class CardActionSyncService {
  readonly #window = inject<Window | null>(WINDOW, { optional: true });

  readonly #channel = this.#window && 'BroadcastChannel' in this.#window ? new BroadcastChannel('zeusm-card-action-sync') : null;

  readonly #listeners = new Set<(event: CardActionSyncEvent) => void>();

  /**
   * Initializes the service and sets up the BroadcastChannel listener.
   */
  constructor() {
    if (!this.#channel) {
      return;
    }

    this.#channel.addEventListener('message', event => {
      this.#notifyListeners(event.data as CardActionSyncEvent);
    });
  }

  /**
   * Emits a card action synchronization event to all tabs and local listeners.
   *
   * This method broadcasts the event through the BroadcastChannel (if available)
   * and also notifies all local listeners in the current tab. Events are only
   * emitted if a valid cardId is provided.
   *
   * @param event - The card action event to broadcast
   *
   */
  emit(event: CardActionSyncEvent): void {
    if (!event.cardId) {
      return;
    }

    if (this.#channel) {
      this.#channel.postMessage(event);
    }

    this.#notifyListeners(event);
  }

  onMessage(listener: (event: CardActionSyncEvent) => void): () => void {
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  }

  /**
   * Notifies all registered listeners about a card action event.
   *
   * @param event - The card action event to broadcast to listeners
   * @private
   */
  #notifyListeners(event: CardActionSyncEvent): void {
    this.#listeners.forEach(listener => listener(event));
  }
}
